import assert from "assert";

// Disttributed ID generator based on Twitter Snowflake algorithm

// Note about the timestamp limitation:
// 42 bits for timestamp will be exhausted in 139.5 years and will run out of space in Jul 2163
// 2^42 / (1000 * 60 * 60 * 24 * 365.25) = 139.5
// 139.5 years from Jan 2024 is Jul 2163

const TIMESTAMP_BITS  = 42n; // Twitter Snowflake originaly used 41 bits for timestamp
const MACHINE_ID_BITS = 10n;
const SEQUENCE_BITS   = 12n;

assert(TIMESTAMP_BITS + MACHINE_ID_BITS + SEQUENCE_BITS === 64n);

const DEFAULT_TIMESTAMP_OFFSET = BigInt(new Date('2024-01-01').getTime());

const MAX_TIMESTAMP  = (1n << TIMESTAMP_BITS ) - 1n;
const MAX_MACHINE_ID = (1n << MACHINE_ID_BITS) - 1n;
const MAX_SEQUENCE   = (1n << SEQUENCE_BITS  ) - 1n;

const isTimestamp = (_: bigint) => _ >= 0n && _ <= MAX_TIMESTAMP;
const isMachineId = (_: bigint) => _ >= 0n && _ <= MAX_MACHINE_ID;
const isSequence  = (_: bigint) => _ >= 0n && _ <= MAX_SEQUENCE;

export class IdGenerator {

  private readonly machineId:       bigint
  private readonly timestampOffset: bigint;

  private lastTimestamp: bigint | undefined = undefined;
  private lastSequence:  bigint | undefined = undefined;

  constructor({ machineId, timestampOffset = DEFAULT_TIMESTAMP_OFFSET }: {
    machineId:        bigint,
    timestampOffset?: bigint
  }) {
    if (machineId === undefined) throw new Error("Machine ID must be defined");
    if (machineId === null     ) throw new Error("Machine ID must be defined");
    if (!isMachineId(machineId)) throw new Error(`Machine ID must be between 0 and ${MAX_MACHINE_ID}`);

    if (timestampOffset === undefined) throw new Error("Timestamp Offset must be defined");
    if (timestampOffset === null     ) throw new Error("Timestamp Offset must be defined");
    if (!isTimestamp(timestampOffset)) throw new Error(`Timestamp Offset must be between 0 and ${MAX_TIMESTAMP}`);

    this.machineId       = machineId;
    this.timestampOffset = timestampOffset;
  }

  public nextId(): bigint {
    const { lastTimestamp, lastSequence, machineId, timestampOffset } = this;

    const { timestamp, sequence } = this.nextSequence();
    assert(isTimestamp(timestamp));
    assert(isSequence (sequence ));

    assert(lastTimestamp === undefined || timestamp >= lastTimestamp);
    assert(lastTimestamp === timestamp && sequence === lastSequence! + 1n
        || lastTimestamp !== timestamp && sequence === 0n);

    this.lastTimestamp = timestamp;
    this.lastSequence  = sequence;

    const adjustedTimestamp = timestamp - timestampOffset;

    const id = (adjustedTimestamp << (MACHINE_ID_BITS + SEQUENCE_BITS))
             | (machineId << SEQUENCE_BITS)
             | sequence;

    return id;
  }

  private nextSequence() {
    const { lastTimestamp, lastSequence } = this;

    const timestamp = BigInt(Date.now());

    if (lastTimestamp === undefined) {
      return this.newSequence({ timestamp });
    }
    else if (timestamp < lastTimestamp) {
      throw new Error("Invalid System Clock!");
    }
    else if (lastSequence === undefined) {
      throw new Error("Sequence is not set");
    }
    else if (lastTimestamp === timestamp && lastSequence === MAX_SEQUENCE) {
      return this.nextMillisSequence({ timestamp });
    }
    else if (lastTimestamp === timestamp) {
      return this.nextCountInSameSequence({ timestamp });
    }
    else {
      return this.newSequence({ timestamp });
    }
  }

  private newSequence({ timestamp }: { timestamp: bigint }) {
    assert(this.lastTimestamp === undefined || this.lastTimestamp < timestamp);
    return {
      timestamp,
      sequence: 0n
    };
  };
  private nextMillisSequence({ timestamp }: { timestamp: bigint }) {
    assert(this.lastTimestamp === timestamp && this.lastSequence === MAX_SEQUENCE);
    return {
      timestamp: waitNextMillis({ timestamp }),
      sequence: 0n
    };
  };
  private nextCountInSameSequence({ timestamp }: { timestamp: bigint }) {
    assert(this.lastTimestamp === timestamp && this.lastSequence !== undefined && this.lastSequence < MAX_SEQUENCE);
    return {
      timestamp,
      sequence: this.lastSequence! + 1n
    };
  };
}

function waitNextMillis({ timestamp }: { timestamp: bigint })
{
  // TODO: refactor this function to use promises and async/await
  assert(timestamp <= BigInt(Date.now()));

  while (true) {
    const newTimestamp = BigInt(Date.now());
    if (newTimestamp < timestamp) throw new Error("Invalid System Clock!");

    if (newTimestamp > timestamp) return newTimestamp;
  }
}
